"use client"

import { useEffect, useState } from "react"
import { cn } from "@/lib/utils"
import { Check } from "lucide-react"

interface EphemeralConfirmationProps {
  message: string
  detail?: string
  onDismiss: () => void
  durationMs?: number
}

export function EphemeralConfirmation({ message, detail, onDismiss, durationMs = 4000 }: EphemeralConfirmationProps) {
  const [isVisible, setIsVisible] = useState(true)
  const [isExiting, setIsExiting] = useState(false)

  useEffect(() => {
    const exitTimer = setTimeout(() => {
      setIsExiting(true)
    }, durationMs - 300) // Start exit animation 300ms before removal

    const removeTimer = setTimeout(() => {
      setIsVisible(false)
      onDismiss()
    }, durationMs)

    return () => {
      clearTimeout(exitTimer)
      clearTimeout(removeTimer)
    }
  }, [durationMs, onDismiss])

  if (!isVisible) return null

  return (
    <div
      className={cn(
        "rounded-xl bg-transparent",
        "border border-[var(--border)]/40", // Thinner border than proposal cards
        "p-3",
        "transition-all duration-300",
        isExiting ? "opacity-0 translate-y-2" : "opacity-100 translate-y-0",
      )}
    >
      <div className="flex items-start gap-2.5">
        <Check className="h-4 w-4 text-blue-500 flex-shrink-0 mt-0.5" />
        <div className="space-y-0.5">
          <p className="text-sm text-[var(--text-primary)] opacity-90 leading-snug">{message}</p>
          {detail && <p className="text-xs text-[var(--text-tertiary)] opacity-60 leading-snug">{detail}</p>}
        </div>
      </div>
    </div>
  )
}
